import React, { useContext, useEffect, useMemo, useState } from "react";
import Edit from "../../../../../../../images/EditProfile.svg";
import Delete from "../../../../../../../images/Delete.svg";
import Close from "../../../../../../../images/Close.svg";
import { TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel
} from "@mui/x-data-grid-pro";

import { useLazyQuery, useMutation } from "@apollo/client";
import { responseType } from "../../../../../../../utils/formTypes";
import { AppContext } from "../../../../../../../context/context";
import useAssetTableJson from "../../../../../../../json/useAssetTableJson";
import {
  GetSuppliersForPOOptionsEdges,
  GetSuppliersForPOOptionsResult,
  GetSuppliersForPOOptionsVars,
  GetSuppliersForPOResult
} from "../../../queries/types";
import useUrlParams from "../../../../../../../customHooks/useUrlParams";

import {
  emptyMessage,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE
} from "../../../../../../../utils/constants";
import useLoggedInUserDetails from "../../../../../../../customHooks/useLoggedInUserDetails";
import {
  GetSuppliersForPO,
  GetSuppliersForPOOptions
} from "../../../../masters/supplier/queries/queries";
import { TableHeaderProps } from "../../../../../../../utils/types";
import {
  HEADER_TEXT_ALIGN,
  MARKS_TEXT_ALIGN,
  TABLE_ROW_HEIGHT
} from "../../../../../../../styles/Variables";
import { TableHeaders } from "../../../../../../../utils/Enum.types";
import { payloadTypes } from "../../../../../../../context/reducer";
import { getModifiedScrollHeight } from "../../../../../../../utils/UtilFunctions";
import { OrderDirection } from "../../../../../../../utils/commonQueryTypes";
import {
  AtsAcLdgrEdge,
  AtsAcLdgrOrderField
} from "../../../../masters/supplier/queries/types";
import { Title } from "../../../../../../../stories/Title/Title";
import Input from "../../../../../../../stories/Input/Input";
import {
  labelClasses,
  LabeledAutocomplete
} from "../../../../../../../styles/AutocompleteStyles";
import { Keys } from "../../../../../../../utils/Enum.keys";
import { StyledDatagrid } from "../../../../../../../styles/DatagridStyles";
import { Button } from "../../../../../../../stories/Button/Button";
import { AtsRfqTrnSupplyEdge } from "../../../quotation/comparision/queries/types";
import useLabels from "../../../../../../../customHooks/useLabels";

interface Props {
  onSelect?: (newValue: responseType) => void;
  setModal: (value: React.SetStateAction<boolean>) => void;
}
const SuppliersList = ({ onSelect, setModal }: Props) => {
  const { state, dispatch } = useContext(AppContext);

  const { Masters_Table } = useAssetTableJson();

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<AtsAcLdgrEdge[]>([]);
  const [searchData, setSearchData] = useState("");
  const { customer_id } = useUrlParams();

  const [message, setMessage] = useState(emptyMessage);
  const { user_details } = useLoggedInUserDetails();

  const [GetSupplier, { data, loading, fetchMore }] = useLazyQuery<
    GetSuppliersForPOResult,
    GetSuppliersForPOOptionsVars
  >(GetSuppliersForPO);

  const dynamicHeaders: TableHeaderProps[] = Masters_Table.Masters.Supplier.map(
    (header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      cellClassName: header.cellClassName,
      hideable: header.hideable
    })
  );

  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSuppliersForPO.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = suppliers.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node
              }
            };
          }
          return newCompany;
        });
        setSuppliers(updatedNewData);
      } else {
        setSuppliers(newData);
      }
      setEndCursor(data.GetSuppliersForPO.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);
  const rowsData = useMemo(() => {
    return suppliers.map(({ node }, index) => ({
      id: index + 1,
      desc: node.desc,
      gstNo: node.supplier_details.gstNo,
      city: node.supplier_details.city,
      state: node.supplier_details.state,
      proficiency: node.supplier_details.proficiency,
      phone: node.supplier_details.phone,
      contactPerson: node.supplier_details.contactPerson,
      supplier_id: node.id
    }));
  }, [suppliers]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetSuppliersForPO.edges;
              const pageInfo = fetchMoreResult.GetSuppliersForPO.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetSuppliersForPO.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetSuppliersForPO: {
                  edges: [...suppliers, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetSuppliersForPO.totalCount! : 0
                }
              };
            }
          });
        }
      }
    };
    if (scrollTable && rowsData.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rowsData]);

  useEffect(() => {
    if (customer_id && state.ActiveFinYr) {
      GetSupplier({
        variables: {
          after: null,
          customer_id,
          finYrId: state.ActiveFinYr.id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsAcLdgrOrderField.CREATED_AT
          },
         
          name: searchData
        }
      });
    }
  }, [customer_id, GetSupplier, state.ActiveFinYr, searchData]);

  const {ledgerLabel}=useLabels();
  return (
    <>
      <div className="suppliers-modal">
        <div className="suppliers-modal--title">
          <Title>{ledgerLabel} List</Title>
          <img src={Close} alt="" onClick={() => setModal(false)} />
        </div>
        <div className=" row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>
        </div>
        <div className="suppliers-modal--tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rowsData}
            rowHeight={TABLE_ROW_HEIGHT}
            onCellClick={(params) => {
              if (params.field === "desc") {
                if (onSelect) {
                  onSelect({
                    label: params.row.desc,
                    value: params.row.item_id
                  } as responseType);
                }
              }
            }}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
        <Button mode="cancel" onClick={() => setModal(false)} />
      </div>
    </>
  );
};

export default SuppliersList;
